import React, { useEffect, useMemo, useState } from "react";
import { Loading } from "../components/Loader";
import { Select } from "../components/Select";
import { useDispatch, useSelector } from "react-redux";
import { Link, useSearchParams } from "react-router-dom";
import DatePicker from "react-datepicker";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  axisStyle,
  gridStyle,
  seriesColor,
  tooltipStyle,
} from "../shared/chartTheme";
import {
  fetchPnlReport,
  selectPnlData,
  selectPnlError,
  selectPnlStatus,
} from "../features/reports/reportsSlice";
import {
  fetchStores,
  selectStores,
  selectStoresStatus,
} from "../features/stores/storesSlice";

function formatMoney(value) {
  const num = Number(value || 0);
  if (Number.isNaN(num)) return "0.00";
  return num.toLocaleString("ru-RU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatPct(value) {
  const num = Number(value || 0);
  if (Number.isNaN(num)) return "0.00%";
  return `${num.toFixed(2)}%`;
}

function formatAxisMoney(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "";
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 10_000) return `${(n / 1000).toFixed(0)}k`;
  return String(Math.round(n));
}

function formatAxisDate(v) {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  return `${day}.${month}`;
}

function formatTooltipDate(v) {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function toIsoLocal(d) {
  if (!d) return "";
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function parseIsoLocal(iso) {
  if (!iso || typeof iso !== "string") return null;
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (!y || !mo || !d) return null;
  return new Date(y, mo - 1, d);
}

function getDefaultDateRange() {
  const today = new Date();
  const first = new Date(today.getFullYear(), today.getMonth(), 1);
  return { startDate: toIsoLocal(first), endDate: toIsoLocal(today) };
}

const CHART_TOOLTIP = tooltipStyle;
const AXIS_TICK = { fill: "var(--c-text-muted)", fontSize: 13 };

export default function AccountingPnlPage() {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const data = useSelector(selectPnlData);
  const status = useSelector(selectPnlStatus);
  const error = useSelector(selectPnlError);
  const stores = useSelector(selectStores);
  const storesStatus = useSelector(selectStoresStatus);

  const defaultRange = useMemo(getDefaultDateRange, []);
  const [storeId, setStoreId] = useState(
    searchParams.get("store_id") || ""
  );
  const [startDate, setStartDate] = useState(
    searchParams.get("start_date") || defaultRange.startDate
  );
  const [endDate, setEndDate] = useState(
    searchParams.get("end_date") || defaultRange.endDate
  );

  useEffect(() => {
    if (storesStatus === "idle") {
      dispatch(fetchStores({ pageSize: 200 }));
    }
  }, [dispatch, storesStatus]);

  useEffect(() => {
    dispatch(
      fetchPnlReport({
        storeId: storeId || undefined,
        startDate,
        endDate,
      })
    );
  }, [dispatch, storeId, startDate, endDate]);

  useEffect(() => {
    const params = {};
    if (storeId) params.store_id = storeId;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    setSearchParams(params, { replace: true });
  }, [storeId, startDate, endDate, setSearchParams]);

  const filtersQs = useMemo(() => {
    const params = new URLSearchParams();
    if (storeId) params.set("store_id", String(storeId));
    if (startDate) params.set("start_date", startDate);
    if (endDate) params.set("end_date", endDate);
    const qs = params.toString();
    return qs ? `?${qs}` : "";
  }, [storeId, startDate, endDate]);

  const handleApply = () => {
    dispatch(
      fetchPnlReport({
        storeId: storeId || undefined,
        startDate,
        endDate,
      })
    );
  };

  const handleReset = () => {
    setStoreId("");
    setStartDate(defaultRange.startDate);
    setEndDate(defaultRange.endDate);
  };

  const totals = data?.totals || {};
  const byStore = Array.isArray(data?.by_store) ? data.by_store : [];
  const byCategory = Array.isArray(data?.by_category) ? data.by_category : [];
  const daily = Array.isArray(data?.daily) ? data.daily : [];
  const topProducts = Array.isArray(data?.top_products)
    ? data.top_products
    : [];

  const dailyChartData = useMemo(
    () =>
      daily.map((d) => ({
        date: d.date,
        revenue: Number(d.revenue) || 0,
        cogs: Number(d.cogs) || 0,
        gross_profit: Number(d.gross_profit) || 0,
        margin_pct: Number(d.margin_pct) || 0,
      })),
    [daily]
  );

  const byStoreChartData = useMemo(
    () =>
      byStore.map((row) => ({
        name: row.store_name,
        revenue: Number(row.revenue) || 0,
        cogs: Number(row.cogs) || 0,
        gross_profit: Number(row.gross_profit) || 0,
      })),
    [byStore]
  );

  const byCategoryChartData = useMemo(() => {
    const rows = byCategory
      .map((row) => ({
        name: row.category_name,
        revenue: Number(row.revenue) || 0,
      }))
      .filter((r) => r.revenue > 0)
      .sort((a, b) => b.revenue - a.revenue);
    if (rows.length <= 8) return rows;
    const top = rows.slice(0, 7);
    const rest = rows.slice(7);
    const restRevenue = rest.reduce((acc, r) => acc + r.revenue, 0);
    return [...top, { name: "Прочие", revenue: restRevenue }];
  }, [byCategory]);

  const topProductsChartData = useMemo(() => {
    return topProducts
      .slice(0, 10)
      .map((p) => ({
        name: p.product_name || "—",
        revenue: Number(p.revenue) || 0,
        quantity: Number(p.quantity) || 0,
      }))
      .reverse();
  }, [topProducts]);

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <div style={styles.title}>Прибыльность (P&L)</div>
          <div style={styles.subtitle}>
            Отчёт по выручке, себестоимости и валовой прибыли
          </div>
        </div>
      </div>

      <div style={styles.toolbar}>
        <div style={styles.field}>
          <label style={styles.label}>Магазин</label>
          <Select
            value={storeId}
            onChange={setStoreId}
            options={[
              { value: "", label: "Все магазины" },
              ...stores.map((s) => ({
                value: String(s.id),
                label: s.name || `Магазин #${s.id}`,
              })),
            ]}
            isSearchable={false}
          />
        </div>
        <div style={styles.field}>
          <label style={styles.label}>С даты</label>
          <DatePicker
            selected={parseIsoLocal(startDate)}
            onChange={(d) => setStartDate(toIsoLocal(d))}
            dateFormat="dd.MM.yyyy"
            placeholderText="дд.мм.гггг"
            customInput={<input style={styles.input} />}
            wrapperClassName="pnl-datepicker-wrapper"
          />
        </div>
        <div style={styles.field}>
          <label style={styles.label}>По дату</label>
          <DatePicker
            selected={parseIsoLocal(endDate)}
            onChange={(d) => setEndDate(toIsoLocal(d))}
            dateFormat="dd.MM.yyyy"
            placeholderText="дд.мм.гггг"
            customInput={<input style={styles.input} />}
            wrapperClassName="pnl-datepicker-wrapper"
          />
        </div>
        <div style={styles.toolbarButtons}>
          <button type="button" onClick={handleApply} style={styles.applyBtn}>
            Обновить
          </button>
          <button type="button" onClick={handleReset} style={styles.resetBtn}>
            Сбросить
          </button>
        </div>
      </div>

      {status === "loading" && (
        <Loading text="Загрузка отчёта…" />
      )}
      {status === "failed" && error && (
        <div style={styles.errorBox}>{error}</div>
      )}

      <div style={styles.body}>
        <div style={styles.kpiGrid}>
          <KpiCard
            title="Выручка"
            value={`${formatMoney(totals.revenue)} сом`}
            hint={`Продажи: ${formatMoney(totals.sale_revenue)}`}
          />
          <KpiCard
            title="Себестоимость"
            value={`${formatMoney(totals.cogs)} сом`}
            hint={`COGS продаж: ${formatMoney(totals.sale_cogs)}`}
          />
          <KpiCard
            title="Валовая прибыль"
            value={`${formatMoney(totals.gross_profit)} сом`}
            hint={`Возвратов: ${formatMoney(totals.refund_revenue)}`}
            accent="var(--c-success)"
          />
          <KpiCard
            title="Маржа"
            value={formatPct(totals.margin_pct)}
            hint={`${totals.sale_count || 0} продаж · ${totals.refund_count || 0} возвратов`}
            accent="var(--c-text)"
          />
        </div>

        <Section title="Динамика по дням">
          {dailyChartData.length > 0 ? (
            <div style={styles.chartCard}>
              <ResponsiveContainer width="100%" height={320}>
                <AreaChart
                  data={dailyChartData}
                  margin={{ top: 16, right: 24, left: 8, bottom: 8 }}
                >
                  <defs>
                    <linearGradient id="grRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="var(--c-s1)" stopOpacity={0.55} />
                      <stop offset="100%" stopColor="var(--c-s1)" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="grCogs" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="var(--c-s5)" stopOpacity={0.45} />
                      <stop offset="100%" stopColor="var(--c-s5)" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="grProfit" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="var(--c-success)" stopOpacity={0.6} />
                      <stop offset="100%" stopColor="var(--c-success)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="var(--c-border)"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="date"
                    tick={AXIS_TICK}
                    tickFormatter={formatAxisDate}
                    axisLine={{ stroke: "var(--c-border)" }}
                    tickLine={{ stroke: "var(--c-border)" }}
                    minTickGap={24}
                  />
                  <YAxis
                    tick={AXIS_TICK}
                    tickFormatter={formatAxisMoney}
                    axisLine={{ stroke: "var(--c-border)" }}
                    tickLine={{ stroke: "var(--c-border)" }}
                    width={56}
                  />
                  <Tooltip
                    contentStyle={CHART_TOOLTIP}
                    labelFormatter={formatTooltipDate}
                    formatter={(value, name) => [`${formatMoney(value)} сом`, name]}
                  />
                  <Legend wrapperStyle={{ fontSize: 14 }} />
                  <Area
                    type="monotone"
                    dataKey="revenue"
                    name="Выручка"
                    stroke="var(--c-s1)"
                    fill="url(#grRevenue)"
                    strokeWidth={2}
                  />
                  <Area
                    type="monotone"
                    dataKey="cogs"
                    name="Себестоимость"
                    stroke="var(--c-s5)"
                    fill="url(#grCogs)"
                    strokeWidth={2}
                  />
                  <Area
                    type="monotone"
                    dataKey="gross_profit"
                    name="Прибыль"
                    stroke="var(--c-success)"
                    fill="url(#grProfit)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div style={styles.empty}>Нет данных за выбранный период</div>
          )}
        </Section>

        <Section title="Маржинальность по дням">
          {dailyChartData.length > 0 ? (
            <div style={styles.chartCard}>
              <ResponsiveContainer width="100%" height={240}>
                <LineChart
                  data={dailyChartData}
                  margin={{ top: 12, right: 24, left: 8, bottom: 8 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="var(--c-border)"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="date"
                    tick={AXIS_TICK}
                    tickFormatter={formatAxisDate}
                    axisLine={{ stroke: "var(--c-border)" }}
                    tickLine={{ stroke: "var(--c-border)" }}
                    minTickGap={24}
                  />
                  <YAxis
                    tick={AXIS_TICK}
                    tickFormatter={(v) => `${Math.round(Number(v))}%`}
                    axisLine={{ stroke: "var(--c-border)" }}
                    tickLine={{ stroke: "var(--c-border)" }}
                    width={48}
                    domain={[0, "auto"]}
                  />
                  <Tooltip
                    contentStyle={CHART_TOOLTIP}
                    labelFormatter={formatTooltipDate}
                    formatter={(value) => [`${Number(value).toFixed(2)}%`, "Маржа"]}
                  />
                  <Line
                    type="monotone"
                    dataKey="margin_pct"
                    name="Маржа %"
                    stroke="var(--c-s4)"
                    strokeWidth={2}
                    dot={{ r: 3, fill: "var(--c-s4)", stroke: "var(--c-s4)" }}
                    activeDot={{ r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div style={styles.empty}>Нет данных за выбранный период</div>
          )}
        </Section>

        <div style={styles.twoColGrid}>
          <Section title="Выручка по магазинам">
            {byStoreChartData.length > 0 ? (
              <div style={styles.chartCard}>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={byStoreChartData}
                    margin={{ top: 12, right: 16, left: 8, bottom: 8 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="var(--c-border)"
                      vertical={false}
                    />
                    <XAxis
                      dataKey="name"
                      tick={AXIS_TICK}
                      axisLine={{ stroke: "var(--c-border)" }}
                      tickLine={{ stroke: "var(--c-border)" }}
                      interval={0}
                      angle={-15}
                      height={50}
                      textAnchor="end"
                    />
                    <YAxis
                      tick={AXIS_TICK}
                      tickFormatter={formatAxisMoney}
                      axisLine={{ stroke: "var(--c-border)" }}
                      tickLine={{ stroke: "var(--c-border)" }}
                      width={56}
                    />
                    <Tooltip
                      contentStyle={CHART_TOOLTIP}
                      formatter={(value, name) => [
                        `${formatMoney(value)} сом`,
                        name,
                      ]}
                    />
                    <Legend wrapperStyle={{ fontSize: 14 }} />
                    <Bar
                      dataKey="revenue"
                      name="Выручка"
                      fill="var(--c-s1)"
                      radius={[6, 6, 0, 0]}
                    />
                    <Bar
                      dataKey="gross_profit"
                      name="Прибыль"
                      fill="var(--c-success)"
                      radius={[6, 6, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div style={styles.empty}>Нет данных по магазинам</div>
            )}
          </Section>

          <Section title="Выручка по категориям">
            {byCategoryChartData.length > 0 ? (
              <div style={styles.chartCard}>
                {/*
                  Горизонтальные бары вместо круга (AUFB-67).
                  Категорий бывает до 12, а различимых цветов в палитре 5:
                  цикл по модулю дал бы две одинаковые доли в одной легенде.
                  Бары одного цвета показывают все категории сразу, читаются
                  точнее круга и не требуют легенды — подписи стоят на оси.
                */}
                <ResponsiveContainer
                  width="100%"
                  height={Math.max(220, byCategoryChartData.length * 34 + 40)}
                >
                  <BarChart
                    data={byCategoryChartData}
                    layout="vertical"
                    margin={{ top: 4, right: 56, bottom: 4, left: 8 }}
                  >
                    <CartesianGrid
                      {...gridStyle}
                      horizontal={false}
                    />
                    <XAxis type="number" hide />
                    <YAxis
                      type="category"
                      dataKey="name"
                      width={140}
                      tickLine={false}
                      axisLine={false}
                      tick={axisStyle.tick}
                    />
                    <Tooltip
                      cursor={{ fill: "var(--c-card-hover)" }}
                      contentStyle={CHART_TOOLTIP}
                      formatter={(value) => [
                        `${formatMoney(value)} сом`,
                        "Выручка",
                      ]}
                    />
                    <Bar
                      dataKey="revenue"
                      name="Выручка"
                      fill={seriesColor(0)}
                      radius={[0, 4, 4, 0]}
                      barSize={18}
                    >
                      <LabelList
                        dataKey="revenue"
                        position="right"
                        formatter={(v) => formatMoney(v)}
                        style={{
                          fill: "var(--c-text-muted)",
                          fontSize: 13,
                          fontVariantNumeric: "tabular-nums",
                        }}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div style={styles.empty}>Нет данных по категориям</div>
            )}
          </Section>
        </div>

        <Section title="Топ-10 товаров по выручке">
          {topProductsChartData.length > 0 ? (
            <div style={styles.chartCard}>
              <ResponsiveContainer
                width="100%"
                height={Math.max(280, topProductsChartData.length * 32)}
              >
                <BarChart
                  data={topProductsChartData}
                  layout="vertical"
                  margin={{ top: 8, right: 24, left: 8, bottom: 8 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="var(--c-border)"
                    horizontal={false}
                  />
                  <XAxis
                    type="number"
                    tick={AXIS_TICK}
                    tickFormatter={formatAxisMoney}
                    axisLine={{ stroke: "var(--c-border)" }}
                    tickLine={{ stroke: "var(--c-border)" }}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    tick={AXIS_TICK}
                    axisLine={{ stroke: "var(--c-border)" }}
                    tickLine={{ stroke: "var(--c-border)" }}
                    width={180}
                  />
                  <Tooltip
                    contentStyle={CHART_TOOLTIP}
                    formatter={(value, name, item) => {
                      if (name === "Выручка") {
                        const qty = item?.payload?.quantity;
                        return [
                          `${formatMoney(value)} сом`,
                          qty != null
                            ? `Выручка (${formatMoney(qty)} шт.)`
                            : "Выручка",
                        ];
                      }
                      return [formatMoney(value), name];
                    }}
                  />
                  <Bar
                    dataKey="revenue"
                    name="Выручка"
                    fill="var(--c-s3)"
                    radius={[0, 6, 6, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div style={styles.empty}>
              Нет продаж за выбранный период
            </div>
          )}
        </Section>

        <Section title="Подробные отчёты">
          <div style={styles.linkCardsGrid}>
            <BreakdownLinkCard
              to={`/accounting/pnl/by-stores${filtersQs}`}
              title="Прибыльность по магазинам"
              description="Полная разбивка по точкам продаж: выручка, себестоимость, прибыль, маржа, продажи и возвраты с сортировкой и итогами."
              metric={
                byStore.length > 0
                  ? `${byStore.length} ${pluralize(byStore.length, [
                      "магазин",
                      "магазина",
                      "магазинов",
                    ])}`
                  : "Нет данных за период"
              }
              accent="var(--c-s1)"
            />
            <BreakdownLinkCard
              to={`/accounting/pnl/by-categories${filtersQs}`}
              title="Прибыльность по категориям"
              description="Полная разбивка по категориям товаров: какие группы приносят больше выручки и прибыли, маржа по группам."
              metric={
                byCategory.length > 0
                  ? `${byCategory.length} ${pluralize(byCategory.length, [
                      "категория",
                      "категории",
                      "категорий",
                    ])}`
                  : "Нет данных за период"
              }
              accent="var(--c-s3)"
            />
          </div>
        </Section>

        <Section title="Топ-20 продаваемых товаров">
          <TopProductsTable rows={topProducts} />
        </Section>
      </div>
    </div>
  );
}

function KpiCard({ title, value, hint, accent }) {
  return (
    <div style={styles.kpiCard}>
      <div style={styles.kpiTitle}>{title}</div>
      <div style={{ ...styles.kpiValue, color: accent || "var(--c-text)" }}>
        {value}
      </div>
      {hint && <div style={styles.kpiHint}>{hint}</div>}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={styles.section}>
      <div style={styles.sectionTitle}>{title}</div>
      {children}
    </div>
  );
}

function pluralize(n, forms) {
  const num = Math.abs(Number(n) || 0) % 100;
  const num1 = num % 10;
  if (num > 10 && num < 20) return forms[2];
  if (num1 > 1 && num1 < 5) return forms[1];
  if (num1 === 1) return forms[0];
  return forms[2];
}

function BreakdownLinkCard({ to, title, description, metric, accent }) {
  return (
    <Link to={to} style={styles.linkCard}>
      <div
        style={{
          ...styles.linkCardAccent,
          background: accent || "var(--c-text)",
        }}
      />
      <div style={styles.linkCardBody}>
        <div style={styles.linkCardHeader}>
          <div style={styles.linkCardTitle}>{title}</div>
          <div style={styles.linkCardArrow}>→</div>
        </div>
        <div style={styles.linkCardDescription}>{description}</div>
        <div style={styles.linkCardFooter}>
          <span
            style={{
              ...styles.linkCardMetric,
              color: accent || "var(--c-text)",
            }}
          >
            {metric}
          </span>
          <span style={styles.linkCardCta}>Открыть подробный отчёт</span>
        </div>
      </div>
    </Link>
  );
}

// eslint-disable-next-line no-unused-vars
function BreakdownTable({ rows, columns, emptyText }) {
  if (!rows || rows.length === 0) {
    return <div style={styles.empty}>{emptyText}</div>;
  }
  return (
    <div style={styles.tableWrap}>
      <table style={styles.table}>
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key} style={styles.th}>
                {c.title}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr
              key={`${row.store_id ?? row.category_id ?? idx}-${idx}`}
              style={styles.tr}
            >
              {columns.map((c) => {
                const raw = row[c.key];
                let display = raw ?? "—";
                if (c.money) display = `${formatMoney(raw)} сом`;
                else if (c.pct) display = formatPct(raw);
                else if (c.numeric)
                  display = Number(raw || 0).toLocaleString("ru-RU");
                return (
                  <td
                    key={c.key}
                    style={{
                      ...styles.td,
                      ...(c.bold ? { fontWeight: 800 } : null),
                      ...(c.style || null),
                      ...(c.money || c.pct || c.numeric
                        ? {
                            textAlign: "right",
                            fontVariantNumeric: "tabular-nums",
                          }
                        : null),
                    }}
                  >
                    {display}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TopProductsTable({ rows }) {
  if (!rows || rows.length === 0) {
    return (
      <div style={styles.empty}>
        Нет продаж за выбранный период
      </div>
    );
  }
  return (
    <div style={styles.tableWrap}>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={{ ...styles.th, width: 48 }}>#</th>
            <th style={styles.th}>Товар</th>
            <th style={styles.th}>Штрих-код</th>
            <th style={{ ...styles.th, textAlign: "right" }}>Кол-во</th>
            <th style={{ ...styles.th, textAlign: "right" }}>Выручка</th>
            <th style={{ ...styles.th, textAlign: "right" }}>Себест.</th>
            <th style={{ ...styles.th, textAlign: "right" }}>Прибыль</th>
            <th style={{ ...styles.th, textAlign: "right" }}>Маржа</th>
            <th style={{ ...styles.th, textAlign: "right" }}>Чеков</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr
              key={`${row.product_id ?? "x"}-${idx}`}
              style={styles.tr}
            >
              <td
                style={{
                  ...styles.td,
                  textAlign: "center",
                  color: "var(--c-text-muted)",
                  fontWeight: 700,
                }}
              >
                {idx + 1}
              </td>
              <td style={{ ...styles.td, fontWeight: 700 }}>
                {row.product_name || "—"}
              </td>
              <td
                style={{
                  ...styles.td,
                  color: "var(--c-text-muted)",
                  fontFamily: "monospace",
                  fontSize: 14,
                }}
              >
                {row.barcode || "—"}
              </td>
              <td
                style={{
                  ...styles.td,
                  textAlign: "right",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {formatMoney(row.quantity)}
              </td>
              <td
                style={{
                  ...styles.td,
                  textAlign: "right",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {formatMoney(row.revenue)} сом
              </td>
              <td
                style={{
                  ...styles.td,
                  textAlign: "right",
                  fontVariantNumeric: "tabular-nums",
                  color: "var(--c-text-muted)",
                }}
              >
                {formatMoney(row.cogs)} сом
              </td>
              <td
                style={{
                  ...styles.td,
                  textAlign: "right",
                  fontVariantNumeric: "tabular-nums",
                  fontWeight: 800,
                  color: "var(--c-success)",
                }}
              >
                {formatMoney(row.gross_profit)} сом
              </td>
              <td
                style={{
                  ...styles.td,
                  textAlign: "right",
                  fontVariantNumeric: "tabular-nums",
                  color: "var(--c-text)",
                }}
              >
                {formatPct(row.margin_pct)}
              </td>
              <td
                style={{
                  ...styles.td,
                  textAlign: "right",
                  fontVariantNumeric: "tabular-nums",
                  color: "var(--c-text-muted)",
                }}
              >
                {Number(row.lines_count || 0).toLocaleString("ru-RU")}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const styles = {
  page: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    background: "var(--c-bg)",
    color: "var(--c-text)",
    overflow: "auto",
  },
  header: {
    padding: "16px 24px",
    borderBottom: "1px solid var(--c-border)",
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: 14,
    flexWrap: "wrap",
    background: "var(--c-bg)",
    flexShrink: 0,
  },
  title: { fontSize: 22, fontWeight: 900, color: "var(--c-text)", margin: 0 },
  subtitle: { fontSize: 15, color: "var(--c-text-muted)", marginTop: 3 },
  toolbar: {
    display: "flex",
    gap: 12,
    alignItems: "flex-end",
    flexWrap: "wrap",
    padding: "12px 24px",
    borderBottom: "1px solid var(--c-border)",
    background: "var(--c-bg)",
    flexShrink: 0,
  },
  field: { display: "flex", flexDirection: "column", gap: 4, minWidth: 160 },
  label: {
    fontSize: 13,
    fontWeight: 600,
    color: "var(--c-text-muted)",
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
  input: {
    padding: "8px 12px",
    borderRadius: 8,
    border: "1px solid var(--c-border)",
    background: "var(--c-input)",
    color: "var(--c-text)",
    outline: "none",
    fontSize: 15,
  },
  select: {
    padding: "8px 12px",
    borderRadius: 8,
    border: "1px solid var(--c-border)",
    background: "var(--c-input)",
    color: "var(--c-text)",
    outline: "none",
    fontSize: 15,
    cursor: "pointer",
    minWidth: 200,
  },
  toolbarButtons: { display: "flex", gap: 8, marginLeft: "auto" },
  applyBtn: {
    padding: "9px 18px",
    borderRadius: 99,
    border: "none",
    background: "var(--c-btn)",
    color: "var(--c-btn-text)",
    cursor: "pointer",
    fontWeight: 700,
    fontSize: 15,
  },
  resetBtn: {
    padding: "9px 16px",
    borderRadius: 99,
    border: "1px solid var(--c-border)",
    background: "transparent",
    color: "var(--c-text-sub)",
    cursor: "pointer",
    fontWeight: 600,
    fontSize: 15,
  },
  notice: { padding: "14px 24px", fontSize: 15, color: "var(--c-text-muted)" },
  errorBox: {
    margin: "0 24px",
    padding: "12px 16px",
    background: "rgba(248,113,113,0.1)",
    border: "1px solid rgba(248,113,113,0.2)",
    borderRadius: 16,
    color: "var(--c-danger)",
    fontSize: 15,
  },
  body: {
    padding: "20px 24px 24px",
    display: "flex",
    flexDirection: "column",
    gap: 24,
  },
  kpiGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 14,
  },
  kpiCard: {
    background: "var(--c-card)",
    border: "1px solid var(--c-border)",
    borderRadius: 16,
    padding: "16px 18px",
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  kpiTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: "var(--c-text-muted)",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  kpiValue: {
    fontSize: 23,
    fontWeight: 900,
    color: "var(--c-text)",
    fontVariantNumeric: "tabular-nums",
  },
  kpiHint: { fontSize: 14, color: "var(--c-text-muted)" },
  section: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  sectionTitle: { fontSize: 16, fontWeight: 800, color: "var(--c-text)" },
  chartCard: {
    background: "var(--c-card)",
    border: "1px solid var(--c-border)",
    borderRadius: 16,
    padding: "16px 12px 8px",
  },
  twoColGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))",
    gap: 24,
  },
  tableWrap: {
    border: "1px solid var(--c-border)",
    borderRadius: 16,
    overflow: "hidden",
    background: "var(--c-card)",
  },
  table: { width: "100%", borderCollapse: "separate", borderSpacing: 0 },
  th: {
    textAlign: "left",
    padding: "10px 14px",
    fontSize: 13,
    fontWeight: 700,
    color: "var(--c-text-sub)",
    background: "var(--c-input)",
    borderBottom: "1px solid var(--c-border)",
    whiteSpace: "nowrap",
    textTransform: "uppercase",
    letterSpacing: "0.4px",
  },
  tr: {},
  td: {
    padding: "11px 14px",
    fontSize: 15,
    borderBottom: "1px solid var(--c-border)",
    verticalAlign: "middle",
    color: "var(--c-text)",
  },
  empty: {
    padding: "24px",
    textAlign: "center",
    fontSize: 15,
    color: "var(--c-text-muted)",
    border: "1px dashed var(--c-border)",
    borderRadius: 12,
    background: "var(--c-card)",
  },
  linkCardsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
    gap: 14,
  },
  linkCard: {
    display: "flex",
    background: "var(--c-card)",
    border: "1px solid var(--c-border)",
    borderRadius: 16,
    overflow: "hidden",
    textDecoration: "none",
    color: "inherit",
    transition: "transform 120ms ease, border-color 120ms ease",
  },
  linkCardAccent: {
    width: 4,
    flexShrink: 0,
  },
  linkCardBody: {
    flex: 1,
    padding: "16px 18px",
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  linkCardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 8,
  },
  linkCardTitle: {
    fontSize: 17,
    fontWeight: 800,
    color: "var(--c-text)",
  },
  linkCardArrow: {
    fontSize: 20,
    fontWeight: 700,
    color: "var(--c-text-muted)",
  },
  linkCardDescription: {
    fontSize: 15,
    lineHeight: 1.5,
    color: "var(--c-text-muted)",
  },
  linkCardFooter: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    paddingTop: 6,
    borderTop: "1px solid var(--c-border)",
    marginTop: 4,
  },
  linkCardMetric: {
    fontSize: 14,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
  linkCardCta: {
    fontSize: 14,
    fontWeight: 700,
    color: "var(--c-text)",
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
};
